/**
 * The website conduct switches — FR-I4, T-4.10, [ADR-0014](../../../../docs/adr/0014-profession-policy-defaults-with-admin-override.md);
 * one shared component since w12b; redrawn by Wave 14E (C13, spec §10.5 item 8,
 * T-4.66).
 *
 * **Drawn in one place.** Since Wave 14 the switches exist once, on *Your
 * website → What you may say* (`/site/policy`, ADR-0032 §3). *Settings →
 * Profession* links there and reads the policy only for a count
 * (`overrideCount`). Until Wave 12 there were two copies, and until Wave 14 one
 * component drawn twice; a switch explained two ways is two answers to a
 * question about a regulator.
 *
 * Five switches, defaulted per profession from the institute's own guidelines,
 * each overridable by the firm **with the source shown at the moment they
 * override it** — ADR-0014 §2, and why the citation travels in each switch's
 * card (*What your institute says*, a collapsible) rather than in a help page.
 *
 * **Grouped by what the switch does, read from `SWITCH_EFFECT`:** the enforced
 * ones — `show_prices_public`, `show_testimonials`, `show_client_names`, which
 * leave the fee or the section out of the render — are *What your site will
 * show*; the recorded ones — `show_comparative_claims` and
 * `allow_third_party_ads`, the last all that is left of spec §9-18 — are *What
 * your institute's guidelines say*. Each card carries the badge too. Telling a
 * firm a switch protects it when nothing reads it is a false assurance about a
 * regulator.
 *
 * **One Save** (ADR-0032 §5): a switch no longer saves on click. Flipping one,
 * or *Back to your institute's default*, changes the page; the edit bar's Save
 * sends one `PUT /firm/policy` per changed switch, in order, and stops at the
 * first refusal with the rest still pending.
 *
 * **Every shipped default is `false`**, and that is a decision: the errors are
 * not the same size. A wrong `true` is ProDesk publishing what an institute
 * forbids, on ProDesk's suggestion; a conservative `false` costs a click, with
 * the permitting clause printed beside it. **Except for `other`** (w13b,
 * ADR-0031): no institute, so no rule — every switch starts on, nothing is
 * cited, and one plain line says so, from the service's `rules: false`.
 */

import { Alert, Badge, Button, Card, CardBody, CardHeader, Skeleton, Switch } from '@fiveox/ui'
import { ChevronRight, ExternalLink, RotateCcw, ShieldCheck } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'

import { SETTINGS, useEvaluation } from '../lib/access'
import { ApiError } from '../lib/api'
import { BASIS_LABELS, firm, type PolicySwitch } from '../lib/api/firm'
import { SWITCH_EFFECT } from '../lib/api/site'
import { EditBar, useDraftPreviewHref, useLeaveGuard } from './edit'

export const SWITCH_LABELS: Record<string, string> = {
  show_prices_public: 'Show your fees on the public site',
  show_client_names: 'Name your clients on your site',
  show_testimonials: 'Show client testimonials',
  show_comparative_claims: 'Make claims that compare you to other firms',
  allow_third_party_ads: 'Allow third-party advertising',
}

/** One line of what the switch does *in the position it is in* — the card's
 *  subtitle. The long form stays in `SWITCH_EFFECT`, under the guideline. */
export const SWITCH_LINE: Record<string, { on: string; off: string }> = {
  show_prices_public: {
    on: 'Your fees appear on your public service pages.',
    off: 'Your fees are left out of the public site. Clients still see them in the portal.',
  },
  show_client_names: {
    on: 'The Client names and Client stories sections appear on your site.',
    off: 'The Client names and Client stories sections are left out of your site.',
  },
  show_testimonials: {
    on: 'The What clients say section appears on your site.',
    off: 'The What clients say section is left out of your site.',
  },
  show_comparative_claims: {
    on: 'You record that you may compare yourself to other firms. The words are yours.',
    off: 'You record that you don’t compare yourself to other firms. The words are yours.',
  },
  allow_third_party_ads: {
    on: 'You record that you allow advertising. Your site has no ad slots either way.',
    off: 'You record that you don’t allow advertising. Your site has no ad slots either way.',
  },
}

export interface ConductPack {
  switches: PolicySwitch[]
  profession: string | null
  regulator: string | null
  read_on: string | null
  defaults_available: boolean
  disclaimers: { required: boolean; text: string; clause: string | null }[]
  disclaimers_note: string
  /** w13b — `false` when no conduct policy applies (`other`, ADR-0031 §2).
   *  Read from the service's flag, not the profession's code (ADR-0014 §5). */
  rules?: boolean | null
}

/** No rule applies to this firm — `rules: false`, the fifth profession. */
export function withoutRules(pack: Pick<ConductPack, 'rules'> | null): boolean {
  return pack?.rules === false
}

/** How many switches the firm has moved off its institute's default — the
 *  number the profession dialog promises it keeps. */
export function overrideCount(pack: Pick<ConductPack, 'switches'> | null): number {
  return pack ? pack.switches.filter((row) => row.overridden).length : 0
}

/** Enforced at render, by omission. Unknown switches are recorded. */
export function isEnforced(name: string): boolean {
  return SWITCH_EFFECT[name]?.enforced === true
}

/** The two groups, in the service's order within each. */
export function groupSwitches<T extends Pick<PolicySwitch, 'switch'>>(
  switches: T[],
): { show: T[]; recorded: T[] } {
  return {
    show: switches.filter((row) => isEnforced(row.switch)),
    recorded: switches.filter((row) => !isEnforced(row.switch)),
  }
}

/** Unsaved changes: a switch → its new value, or `null` for *back to the
 *  default*. A switch absent from the map is as saved. */
export type PendingPolicy = Record<string, boolean | null>

/** What a switch shows with the pending change applied. */
export function shownSwitch(
  row: PolicySwitch,
  pending: PendingPolicy,
): { value: boolean; overridden: boolean; changed: boolean } {
  if (!(row.switch in pending)) {
    return { value: row.value === true, overridden: row.overridden, changed: false }
  }
  const next = pending[row.switch]
  return next === null
    ? { value: row.default === true, overridden: false, changed: true }
    : { value: next === true, overridden: true, changed: true }
}

/** Flip a switch. Flipping it back to where it was saved clears the change
 *  rather than recording an override nobody asked for. */
export function flipSwitch(row: PolicySwitch, pending: PendingPolicy, value: boolean): PendingPolicy {
  const next = { ...pending }
  const backToSaved = row.overridden
    ? value === (row.value === true)
    : value === (row.default === true)
  if (backToSaved) delete next[row.switch]
  else next[row.switch] = value
  return next
}

/** *Back to your institute's default*. On a switch that was never overridden
 *  it only drops the pending flip. */
export function resetSwitch(row: PolicySwitch, pending: PendingPolicy): PendingPolicy {
  const next = { ...pending }
  if (row.overridden) next[row.switch] = null
  else delete next[row.switch]
  return next
}

/** The requests Save sends — one `PUT /firm/policy` per changed switch, in
 *  the page's order. */
export function policyRequests(
  switches: Pick<PolicySwitch, 'switch'>[],
  pending: PendingPolicy,
): Array<{ switch: string; value: boolean | null }> {
  return switches
    .filter((row) => row.switch in pending)
    .map((row) => ({ switch: row.switch, value: pending[row.switch] ?? null }))
}

function Guideline({ row }: { row: PolicySwitch }) {
  const source = row.source
  const effect = SWITCH_EFFECT[row.switch]
  return (
    <details className="group border-border rounded-xl border" data-guideline="">
      <summary className="text-foreground hover:bg-muted flex cursor-pointer list-none items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium [&::-webkit-details-marker]:hidden">
        <ChevronRight
          size={16}
          className="text-muted-foreground shrink-0 transition-transform group-open:rotate-90"
          aria-hidden
        />
        <span className="whitespace-nowrap">What your institute says</span>
        {row.basis && (
          <span className="text-muted-foreground hidden min-w-0 truncate font-normal sm:inline">
            — {BASIS_LABELS[row.basis]}
          </span>
        )}
      </summary>
      <div className="space-y-2 px-3 pt-1 pb-3 text-sm">
        {row.basis && <p className="text-foreground font-medium">{BASIS_LABELS[row.basis]}</p>}
        {source?.quote && (
          <blockquote className="border-primary/40 text-muted-foreground border-l-2 pl-3 italic">
            “{source.quote}”
          </blockquote>
        )}
        {source?.note && <p className="text-muted-foreground">{source.note}</p>}
        {source?.source?.title && (
          <p className="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span>
              {source.source.title}
              {source.clause ? `, ${source.clause}` : ''}
            </span>
            {source.source.url && (
              <a
                href={source.source.url}
                target="_blank"
                rel="noreferrer"
                className="text-primary inline-flex items-center gap-0.5 font-medium hover:underline"
              >
                Read it <ExternalLink size={12} aria-hidden />
              </a>
            )}
            {/* `primary` was read from the institute's own document;
                `secondary` was not, and a firm relying on this is entitled
                to know which. */}
            <Badge size="sm">
              {source.source.kind === 'primary' ? 'From the institute' : 'From a published reproduction'}
            </Badge>
          </p>
        )}
        {effect && (
          <p className="text-muted-foreground border-border border-t pt-2 text-xs">{effect.effect}</p>
        )}
      </div>
    </details>
  )
}

function SwitchCard({
  row,
  pending,
  free,
  editable,
  busy,
  onFlip,
  onReset,
}: {
  row: PolicySwitch
  pending: PendingPolicy
  free: boolean
  editable: boolean
  busy: boolean
  onFlip: (value: boolean) => void
  onReset: () => void
}) {
  const label = SWITCH_LABELS[row.switch] ?? row.switch
  const shown = shownSwitch(row, pending)
  const line = SWITCH_LINE[row.switch]
  const enforced = isEnforced(row.switch)
  const defaultWord = free ? 'the default' : 'your institute’s default'

  return (
    <li
      data-switch={row.switch}
      data-changed={shown.changed ? '' : undefined}
      className={`bg-surface-raised rounded-xl border p-3.5 transition-colors sm:p-5 ${
        shown.changed ? 'border-primary/50' : 'border-border'
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="text-foreground text-sm font-semibold">{label}</h3>
            <span data-enforcement="" className="inline-flex">
              <Badge tone={enforced ? 'success' : 'neutral'} size="sm">
                {enforced ? 'Enforced on your site' : 'Recorded, not enforced'}
              </Badge>
            </span>
          </div>
          <p className="text-muted-foreground mt-1 text-sm">
            {line ? (shown.value ? line.on : line.off) : SWITCH_EFFECT[row.switch]?.effect}
          </p>
        </div>
        <Switch
          srLabel={label}
          checked={shown.value}
          disabled={!editable || busy}
          onChange={(e) => onFlip(e.target.checked)}
          className="mt-0.5 shrink-0"
        />
      </div>

      {/* ADR-0014 §2: the default and its source, at the moment of the
          override. w13b — `other` cites nothing, so there is nothing to open. */}
      {!free && row.basis && (
        <div className="mt-3">
          <Guideline row={row} />
        </div>
      )}

      <div className="text-muted-foreground mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {shown.changed ? (
          <span className="text-primary font-medium">
            {shown.overridden ? 'Changed — not saved yet.' : `Back to ${defaultWord} — not saved yet.`}
          </span>
        ) : shown.overridden ? (
          <span>
            You changed this from {defaultWord}
            {row.set_at ? ` on ${row.set_at.slice(0, 10)}` : ''}.
          </span>
        ) : (
          <span>{free ? 'The default — no rule behind it.' : <>Your institute&rsquo;s default.</>}</span>
        )}
        {editable && shown.overridden && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={onReset} data-reset="">
            <RotateCcw size={12} aria-hidden />
            {free ? 'Back to the default' : <>Back to your institute&rsquo;s default</>}
          </Button>
        )}
      </div>
    </li>
  )
}

function Group({
  title,
  description,
  group,
  children,
}: {
  title: string
  description: string
  group: string
  children: ReactNode
}) {
  return (
    <Card data-group={group}>
      <CardBody className="space-y-4 p-4 sm:p-6">
        <div>
          <h2 className="text-foreground text-base font-semibold">{title}</h2>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
        <ul className="space-y-3">{children}</ul>
      </CardBody>
    </Card>
  )
}

export function ConductSwitches({
  version = 0,
  onLoaded,
}: {
  /** Bumped by the parent after something this reads has changed — a new
   *  profession moves every default. */
  version?: number
  onLoaded?: (pack: ConductPack) => void
}) {
  const { can } = useEvaluation()
  // The policy is the firm's, not the website's: it governs what may be said
  // anywhere, and `settings` is the resource that gates the firm's own record.
  const editable = can(SETTINGS, 'can_update')
  const previewHref = useDraftPreviewHref()

  const [pack, setPack] = useState<ConductPack | null>(null)
  const [pending, setPending] = useState<PendingPolicy>({})
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const body = await firm.policy()
    const next = body.data as ConductPack
    setPack(next)
    setPending({})
    onLoaded?.(next)
    // `onLoaded` is the parent’s; a new function each render must not re-read.
  }, [])

  useEffect(() => {
    void load().catch(() => setLoadError('Your conduct settings could not be loaded.'))
  }, [load, version])

  const requests = useMemo(
    () => (pack ? policyRequests(pack.switches, pending) : []),
    [pack, pending],
  )
  const dirty = requests.length > 0
  const guard = useLeaveGuard(dirty)

  const save = async () => {
    if (!pack || !dirty) return
    setSaving(true)
    setError(null)
    let switches = pack.switches
    const left: PendingPolicy = { ...pending }
    try {
      // One request per switch, in order — the service takes one at a time.
      for (const request of requests) {
        const body = await firm.setPolicy(request)
        switches = body.data.switches
        delete left[request.switch]
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'That could not be saved. The switches not saved yet are still marked.',
      )
    } finally {
      const next = { ...pack, switches }
      setPack(next)
      setPending(left)
      onLoaded?.(next)
      setSaving(false)
    }
  }

  if (!pack) {
    return loadError ? (
      <Alert tone="error">{loadError}</Alert>
    ) : (
      <div className="space-y-3" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-24 w-full rounded-2xl" />
        ))}
      </div>
    )
  }

  const free = withoutRules(pack)
  const groups = groupSwitches(pack.switches)
  const overrides = pack.switches.filter((row) => shownSwitch(row, pending).overridden).length

  const card = (row: PolicySwitch) => (
    <SwitchCard
      key={row.switch}
      row={row}
      pending={pending}
      free={free}
      editable={editable}
      busy={saving}
      onFlip={(value) => setPending((was) => flipSwitch(row, was, value))}
      onReset={() => setPending((was) => resetSwitch(row, was))}
    />
  )

  return (
    <div className="space-y-6" data-conduct-switches="">
      {guard}
      {error && <Alert tone="error">{error}</Alert>}

      {!pack.defaults_available && !free && (
        <Alert tone="warning">
          Your firm has not recorded a profession yet, so there are no guidelines to start from.{' '}
          <Link to="/settings/profession" className="font-medium underline">
            Choose your profession
          </Link>
          .
        </Alert>
      )}

      {free ? (
        // w13b — ADR-0031 §2: the one plain line for the fifth profession.
        <div data-no-conduct-policy="">
          <Alert tone="info">
            No conduct rules apply to your profession in ProDesk. Every switch starts on, nothing is
            cited and no disclaimer is added — set each one as you see fit.
          </Alert>
        </div>
      ) : (
        pack.regulator && (
          <Card data-guidelines-summary="">
            <CardBody className="flex items-start gap-3 p-4 sm:p-6">
              <span className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                <ShieldCheck size={18} aria-hidden />
              </span>
              <div className="min-w-0 space-y-1 text-sm">
                <p className="text-foreground font-medium">
                  Starting from {pack.regulator}’s guidelines
                  {pack.read_on ? `, as read on ${pack.read_on}` : ''}
                </p>
                <p className="text-muted-foreground">
                  {overrides === 0
                    ? 'Every switch is at your institute’s default.'
                    : `You have changed ${overrides === 1 ? 'one switch' : `${overrides} switches`} from your institute’s default.`}{' '}
                  If your institute has since changed its guidelines, yours is the more recent
                  reading — tell us and we will update ours.
                </p>
              </div>
            </CardBody>
          </Card>
        )
      )}

      {free ? (
        <Group
          group="all"
          title="Your switches"
          description="What your site shows is yours to decide. The badge says whether ProDesk acts on the switch."
        >
          {pack.switches.map(card)}
        </Group>
      ) : (
        <>
          {groups.show.length > 0 && (
            <Group
              group="show"
              title="What your site will show"
              description="ProDesk acts on these: off, the fee or the section is left out of your site entirely — not hidden, absent. Nothing you wrote is deleted."
            >
              {groups.show.map(card)}
            </Group>
          )}
          {groups.recorded.length > 0 && (
            <Group
              group="recorded"
              title="What your institute’s guidelines say"
              description="These record your position. ProDesk can’t read your words and judge them, so it doesn’t act on them — the responsibility is yours."
            >
              {groups.recorded.map(card)}
            </Group>
          )}
        </>
      )}

      {pack.disclaimers.length > 0 && (
        <Card data-disclaimers="">
          <CardHeader
            title="What appears in your footer"
            description="Printed at the bottom of every page of your site."
            className="p-4 pb-0 sm:p-6 sm:pb-0"
          />
          <CardBody className="space-y-3 p-4 sm:p-6">
            {pack.disclaimers.map((disclaimer, i) => (
              <div key={i} className="border-border bg-surface-raised rounded-xl border p-4">
                <p className="text-foreground text-sm">{disclaimer.text}</p>
                <p className="text-muted-foreground mt-1 text-xs">
                  {disclaimer.required
                    ? `Required by your institute${disclaimer.clause ? ` — ${disclaimer.clause}` : ''}.`
                    : 'Your institute prescribes no wording. This is a common formulation and it is not required.'}
                </p>
              </div>
            ))}
            {pack.disclaimers_note && (
              <p className="text-muted-foreground text-xs">{pack.disclaimers_note}</p>
            )}
          </CardBody>
        </Card>
      )}

      <p className="text-muted-foreground max-w-2xl text-xs leading-relaxed">
        Nothing third-party runs on your site in any case: no scripts, no tracking pixels, no ad
        slots.
      </p>

      <EditBar
        dirty={dirty}
        saving={saving}
        editable={editable}
        onSave={() => void save()}
        onDiscard={() => setPending({})}
        previewHref={previewHref}
        saveLabel={requests.length > 1 ? `Save ${requests.length} changes` : 'Save'}
      />
    </div>
  )
}
